import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as SPREAD_CONFIG_LIQSRC from '@components/model-structure/liquidity-sources/liquidity-sources.spread.config';
import * as GC from '@grapecity/spread-sheets';
import { capitalizeString } from '@helpers/capitalizeString';
import { trimString } from '@helpers/trimString';
import { BalanceSheetCategory } from '@models/balance-sheet-category';
import { EquationsTreeNode } from '@models/equations-tree-node';
import { AuxiliaryAccountPeriodEquation } from '@models/period-equation';
import { AppConfigAPI } from '@models/types/appconfig.d';
import { AppConfigProvider } from '@root/app/app.config.provider';
import { BalanceSheetService } from '@services/balance-sheet/balance-sheet.service';
import { forkJoin, Observable } from 'rxjs';
import { defaultIfEmpty, map } from 'rxjs/operators';
import { EquationsService } from '../equations/equations.service';
import { TrackingService } from '../tracking/tracking.service';

const SPREADCONFIG = SPREAD_CONFIG_LIQSRC.SpreadConfig;

@Injectable({ providedIn: 'root' })
export class AuxiliaryAccountsService {
  static preparedObject: any = {};
  public sourceIdentifiers = [];
  public myUserId: number = null;
  public onLoadTimestamp: number = null;
  public onEditTimestamp: number = null;
  private environment: AppConfigAPI;

  constructor(
    private http: HttpClient,
    private eqService: EquationsService,
    private tracking: TrackingService,
    private appConfig: AppConfigProvider,
    private readonly balanceSheetService: BalanceSheetService
  ) {
    this.appConfig.environment.subscribe(env => {
      this.environment = env.API;
    });
  }

  // Gets all the Auxiliary Accounts
  public getAllAuxiliaryAccounts(): Promise<object[]> {
    const accountUrl = `${this.environment.BALANCEAPI}/api/balancesheet/auxiliary`;
    const balanceSheetCategories$ = this.balanceSheetService.getCategories() as Observable<BalanceSheetCategory[]>;
    const auxiliaryAccounts$ = this.http.get<object[]>(accountUrl).pipe(defaultIfEmpty([]));
    return forkJoin([auxiliaryAccounts$, balanceSheetCategories$])
      .pipe(
        map(([accounts, categories]) => {
          accounts.map(acc => {
            const foundCategory = categories.find(cat => cat.id.toString() === acc.category);
            if (foundCategory) {
              acc.category_name = foundCategory.name;
            }
            return acc;
          });
          return accounts;
        })
      )
      .toPromise()
      .catch(() => []);
  }

  public async getAllAuxiliaryAccountsPeriodEquations() {
    const sources = await this.getAllAuxiliaryAccounts();
    const listOfIds = sources.map(source => source['auxiliary_mnemonic']);
    const allEquations = (await this.eqService.getAuxiliaryPeriodEquations()) as AuxiliaryAccountPeriodEquation[];
    const hasTemp = allEquations.some(equation => equation.is_temp);
    this.tracking.updateModelDataChangesFlags('auxiliaryEquations', hasTemp, 'getAuxiliaryPeriodEquations');
    return allEquations;
  }

  /**
   * Saves the period equations in the database
   * @param {object[]} spreadContent
   * @param {string} auxiliary_mnemonic
   * @memberof LiquiditySourcesService
   */
  public async savePeriodEquations(spreadContent: object[], auxiliary_mnemonic: string) {
    return await this.eqService.savePeriodEquations('AuxiliaryAccountsPeriodEquations', spreadContent, auxiliary_mnemonic);
  }

  /**
   * Checks if exists a previous temporary record.
   * If it does, returns the data. If it does not exist, returns false
   */
  public async getTemporaryRecordForPeriodEquations(resourceEquationsPath: string, resourceId: string): Promise<Object[]> {
    const url = `${this.environment.TRACKINGAPI}/api/tracking${resourceEquationsPath}?check_temp=1&resource_id=${resourceId}`;
    const temporaryRecord = await this.http
      .get<object[]>(url)
      .toPromise()
      .catch(() => null);
    return temporaryRecord;
  }

  /*
   **  TREE-related methods
   */
  public treePreBuild(apiData) {
    if (apiData) {
      const auxAccountCategories = {};
      apiData.forEach(row => {
        const type = capitalizeString(row['category_name']);

        const key = row['account_name'];
        const value = row['mnemonic'];
        if (!auxAccountCategories[type]) {
          auxAccountCategories[type] = {};
        }
        auxAccountCategories[type][key] = value;
      });

      const newTree = { ...auxAccountCategories };
      return newTree;
    }
    return false;
  }

  public treeBuildNodes(obj: { [key: string]: any }, level: number): EquationsTreeNode[] {
    return Object.keys(obj)
      .sort()
      .reduce<EquationsTreeNode[]>((accumulator, key) => {
        const value = obj[key];
        const node = new EquationsTreeNode();
        node.name = key;

        if (value !== null) {
          if (typeof value === 'object') {
            node.children = this.treeBuildNodes(value, level + 1);
          } else {
            node.value = value;
          }
        }

        return accumulator.concat(node);
      }, []);
  }

  /*
   * Parses the spreadsheed content, applying autoconversions
   */
  public parseSpreadContentForPeriodEquations(spreadContent: object[]): object[] {
    const columnsConfig = SPREADCONFIG.columnsConfig;
    if (spreadContent && spreadContent.hasOwnProperty('length') && spreadContent.length > 0) {
      spreadContent.forEach((row, index) => {
        for (const key in row) {
          if (row.hasOwnProperty(key)) {
            let value = row[key];
            // Purge values
            // -- 1. Trim all strings and convert null values to empty strings
            value = trimString(value);

            // 2. Convert null and undefined to empty string
            if (!value && value !== 0) {
              value = '';
            }

            // -- 3. Rules by column
            switch (key) {
              // Equation
              case columnsConfig[1].name:
                value = `${value}`; // Makes it string (bug fix 2286)
                break;
            }
            spreadContent[index][key] = value;
          }
        }
      });

      return spreadContent || [];
    }
    return [];
  }

  public serializePeriodEquationsData(spreadContent) {
    // tslint:disable-next-line:quotemark
    const processedData = JSON.stringify(spreadContent).replace(/\'/g, "''");
    return processedData;
  }

  public storeTempPeriodEquations(spreadContent: object[], resourceId: string): Promise<any> {
    return this.eqService.storeTempAuxiliaryPeriodEquations(spreadContent, resourceId);
  }

  // ===================
  //   Validator rules
  // ====================

  // Should be a number
  public getShouldBeNumberValidator() {
    const dv = GC.Spread.Sheets.DataValidation.createNumberValidator(
      GC.Spread.Sheets.ConditionalFormatting.ComparisonOperators.greaterThanOrEqualsTo,
      0,
      -1,
      true
    );
    dv.showInputMessage(false);
    return dv;
  }

  // Should be string with a minimum and maximum length (db-limited)
  public getShouldBeStringValidator() {
    const dv = GC.Spread.Sheets.DataValidation.createTextLengthValidator(
      GC.Spread.Sheets.ConditionalFormatting.ComparisonOperators.between,
      '1',
      '50'
    );
    dv.showInputMessage(false);
    return dv;
  }

  // Should be one of the accepted values included in the list
  public getTypeValidator() {
    const strAcceptedTypes = SPREADCONFIG.LIQ_SOURCES_ACCEPTED_TYPE_VALUES.join(',');
    const dv = GC.Spread.Sheets.DataValidation.createListValidator(strAcceptedTypes);
    dv.showInputMessage(false);
    dv.ignoreBlank(false);
    return dv;
  }

  public getNameValidator(rowIndex: number = 0) {
    // Validation Conditions:
    // -- Default value is allowed
    // -- Should be text
    // -- Cannot be empty
    // -- Cannot contain special characters as wildcards or matematical operations
    const columnLetter = 'A';
    const sheetRowNum = rowIndex + 1;

    const validationFormulas = this.getValidationFormulas('name')(columnLetter, sheetRowNum);
    const conditionFinalFormula = this.conditionBuilder(validationFormulas, sheetRowNum);

    const validator = GC.Spread.Sheets.DataValidation.createFormulaValidator(conditionFinalFormula);
    validator.ignoreBlank(false);
    return validator;
  }

  public getMnemonicValidator(rowIndex: number = 0) {
    // Validation Conditions:
    //   -- Mnemonic can't be empty
    //   -- Mnemonic should only accept letters, numbers, underscores (_). It should not allow spaces.
    //   -- Mnemonic should be unique in his column
    //   -- Fix 4781: The mnemonics cannot contain any of the liquidity Types (case-insensitive)

    const columnLetter = 'B';
    const sheetRowNum = rowIndex + 1;

    const validationFormulas = this.getValidationFormulas('mnemonic')(columnLetter, sheetRowNum);
    const conditionFinalFormula = this.conditionBuilder(validationFormulas, sheetRowNum);

    const validator = GC.Spread.Sheets.DataValidation.createFormulaValidator(conditionFinalFormula);
    validator.ignoreBlank(false);
    return validator;
  }

  public getValidationFormulas(forWhatAreRequested) {
    // Wrapper Functions & helpers
    const isPositiveNumber = (columnLetter, sheetRowNum) => `(
      AND(
        ISNUMBER(${columnLetter}${sheetRowNum}),
        (${columnLetter}${sheetRowNum}>=0)
      )
    )`;

    const noWildcards = (columnLetter, sheetRowNum) => `(
      NOT(OR(
          ISNUMBER(SEARCH("~?",${columnLetter}${sheetRowNum})),
          ISNUMBER(SEARCH("~*",${columnLetter}${sheetRowNum})),
          ISNUMBER(SEARCH("~~",${columnLetter}${sheetRowNum}))
        )
      )
    )`;

    const hasOnlyAcceptedCharacters = (charactersAllowed, columnLetter, sheetRowNum) => `(
      IF(
        TRIM(${columnLetter}${sheetRowNum})="",
        TRUE,
        IF( NOT(ISERROR(SUMPRODUCT(SEARCH(MID(${columnLetter}${sheetRowNum},
          ROW(INDIRECT("1:"&LEN(${columnLetter}${sheetRowNum}))),1),
          "${charactersAllowed}")))),
          TRUE,
          FALSE
        )
      )
    )`;

    const notContainsLiqSourceType = (columnLetter, sheetRowNum) => {
      const conditions = [];
      SPREADCONFIG.LIQ_SOURCES_ACCEPTED_TYPE_VALUES.forEach(type => {
        // CHECK AGAINST EACH LIQ.SRC TYPE
        // CASE INSENSITIVE COMPARISON
        conditions.push(`NOT(TRIM(${columnLetter}${sheetRowNum})="${type}")`);
      });

      const formula = `AND(${conditions.join(', ')})`;
      return formula;
    };

    const isZero = (columnLetter, sheetRowNum) => `${columnLetter}${sheetRowNum} = 0`;

    const isUnique = (columnLetter, sheetRowNum) => `(
      OR(
        (COUNTIF(${columnLetter}:${columnLetter}, ${columnLetter}${sheetRowNum}) = 1)
      )
    )`;

    const nonEmpty = (columnLetter, sheetRowNum) => `(
      AND(
        LEN(${columnLetter}${sheetRowNum}) > 0,
        NOT(
          OR(
            ISBLANK(${columnLetter}${sheetRowNum}),
            ${columnLetter}${sheetRowNum}=""
          )
        )
      )
    )`;

    // Functions
    // --- For Name
    const nameFn = (columnLetter, sheetRowNum) => ({
      noWildcards: noWildcards(columnLetter, sheetRowNum),
      nonEmpty: nonEmpty(columnLetter, sheetRowNum),
    });

    // --- For Mnemonic
    const mnemonicFn = (columnLetter, sheetRowNum) => ({
      noWildcards: noWildcards(columnLetter, sheetRowNum),
      hasOnlyAcceptedCharacters: hasOnlyAcceptedCharacters(SPREADCONFIG.MNEMONIC_ACCEPTED_CHARACTERS, columnLetter, sheetRowNum),
      nonEmpty: nonEmpty(columnLetter, sheetRowNum),
      isUnique: isUnique(columnLetter, sheetRowNum),
      // Fix 4781: The mnemonic should not contain values equivalent to Liq.Source Types (case insensitive)
      notContainsLiqSourceType: notContainsLiqSourceType(columnLetter, sheetRowNum),
    });

    // --- For draw_order
    const drawOrderFn = (columnLetter, sheetRowNum) => ({
      noWildcards: noWildcards(columnLetter, sheetRowNum),
      nonEmpty: nonEmpty(columnLetter, sheetRowNum),
      isPositiveNumber: isPositiveNumber(columnLetter, sheetRowNum),
      isUnique: isUnique(columnLetter, sheetRowNum),
    });

    // Switcher
    let response;
    switch (forWhatAreRequested) {
      case 'mnemonic':
        response = mnemonicFn;
        break;
      case 'name':
        response = nameFn;
        break;
      case 'draw_order':
        response = drawOrderFn;
        break;
    }

    if (response) {
      return response;
    } else {
      throw new Error(`Unable to return a validation Formula in getValidationFormulas for ${forWhatAreRequested}`);
    }
  }

  private conditionBuilder(validationFormulas, sheetRow) {
    const isDefaultRow = sheetRowNum => `AND(
      A${sheetRowNum}="",
      B${sheetRowNum}="",
      C${sheetRowNum}="",
      D${sheetRowNum}="",
      E${sheetRowNum}=""
    )`;

    let conditionFinalFormula = `OR(
      ${isDefaultRow(sheetRow)},
      AND(
    `;

    Object.keys(validationFormulas).forEach((key, i) => {
      // Add concatenator
      if (i > 0 && i < Object.keys(validationFormulas).length) {
        // Each TRUE is interpreted as 1 in a sum. So, the sum of all true values should be equal to the length of the array
        conditionFinalFormula += `,`;
      }
      // Add formula
      conditionFinalFormula += `${validationFormulas[key]}`;
    });

    conditionFinalFormula += `))`;
    return conditionFinalFormula;
  }

  // To check if the sheet is valid
  // --- Generics

  public isValidString(value, minLength: number = 1, maxLength: number = null): boolean {
    if (typeof value === 'string') {
      value = trimString(value);
      return value.length >= minLength && (maxLength === null ? true : value.length <= maxLength);
    }
    return false;
  }

  public isValidNumber(value: any, min: number = -2147483647, max: number = 2147483647): boolean {
    return typeof value === 'number' && value >= min && value <= max;
  }

  public customStringValidator(cellValue: any): boolean {
    if (typeof cellValue === 'string') {
      cellValue = trimString(cellValue);
      return true;
    }
    return false;
  }

  // --- Specifics
  public isValidName(value: any): boolean {
    return this.isValidString(value, 2, 50) || false;
  }

  public isValidMnemonic(value: any, spreadContent): boolean {
    // Wrapper autoexecuted functions
    // ------------------------------

    const hasOnlyValidCharacters = ((): boolean => {
      // Contains only accepted characters as:
      // --- numbers
      // --- lowerCase or uppercase letters
      // --- underscore (_)
      // consequently it doesn't accepts spaces between. It should be one word
      value = trimString(`${value}`) || '';
      return (value && this.isValidString(value, 1, 100) && value.match(SPREADCONFIG.MNEMONIC_REGEXP)) || false;
    })();

    const isUnique = (() => {
      const mnemonics = spreadContent
        .map(row => (typeof row['mnemonic'] === 'string' ? row['mnemonic'].trim() : row['mnemonic']))
        .filter(elem => elem !== null || elem !== '');
      return mnemonics.filter(elem => elem === value).length === 1;
    })();

    return (typeof value === 'string' && value.trim() === '') || (isUnique && hasOnlyValidCharacters) || false;
  }

  public generateSidMap(sources, sourcesOriginal = null): { auxiliary_mnemonic: string; mnemonic: string }[] {
    // Reset mapping
    this.sourceIdentifiers = [];

    // Perform mapping
    if (sources) {
      sources = sources.map((source, index) => {
        const identifier = {
          auxiliary_mnemonic: source['auxiliary_mnemonic'] || 0,
          mnemonic: source['mnemonic'] || null,
        };

        if (sourcesOriginal && !source['auxiliary_mnemonic'] && sourcesOriginal[index]['auxiliary_mnemonic']) {
          identifier['auxiliary_mnemonic'] = sourcesOriginal[index]['auxiliary_mnemonic'] || 0;
        }

        if (identifier.auxiliary_mnemonic && identifier.mnemonic) {
          this.sourceIdentifiers.push(identifier);
        }

        return source;
      });

      return sources;
    }
    return null;
  }
}
